import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowDown } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { useContent } from "@/context/ContentContext";
import { PORTRAIT } from "@/assets";

const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

const MaskLine = ({ children, delay = 0, className = "" }) => (
  <span className="line-mask">
    <motion.span
      className={`block ${className}`}
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, ease: [0.22, 1, 0.36, 1], delay }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const { t } = useLang();
  const { content } = useContent();
  const portrait = content.portrait || PORTRAIT;
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const scaleImg = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const yText = useTransform(scrollYProgress, [0, 1], [0, -80]);

  return (
    <section id="hero" ref={ref} data-testid="hero-section" className="relative min-h-screen overflow-hidden">
      {/* Parallax portrait */}
      <motion.div style={{ y: yImg, scale: scaleImg }} className="absolute inset-0 z-0">
        <img src={portrait} alt="Neil Simon" className="w-full h-full object-cover object-top opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-[#050505]" />
        <div className="absolute inset-0" style={{ background: "radial-gradient(60% 50% at 70% 30%, rgba(147,51,234,0.35), transparent 70%)" }} />
      </motion.div>

      <motion.div style={{ y: yText }} className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 min-h-screen flex flex-col justify-center pt-24">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.8 }}
          className="text-xs md:text-sm font-bold tracking-[0.3em] uppercase text-purple-400 mb-6"
          data-testid="hero-kicker"
        >
          {t.hero.kicker}
        </motion.p>

        <h1 className="font-display uppercase leading-[0.82] text-6xl sm:text-8xl lg:text-[9.5vw]">
          <MaskLine delay={0.25}>{t.hero.l1}</MaskLine>
          <MaskLine delay={0.4} className="text-purple-500">{t.hero.l2}</MaskLine>
          <MaskLine delay={0.55} className="stroke-text">{t.hero.l3}</MaskLine>
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.8 }}
          className="mt-10 flex flex-col sm:flex-row gap-4"
        >
          <button
            data-testid="hero-join-btn"
            onClick={() => scrollTo("volunteer")}
            className="px-8 py-4 rounded-full bg-white text-black font-semibold tracking-wide hover:bg-purple-500 hover:text-white transition-colors"
          >
            {t.hero.cta_join}
          </button>
          <button
            data-testid="hero-donate-btn"
            onClick={() => scrollTo("donate")}
            className="px-8 py-4 rounded-full border border-white/25 font-semibold tracking-wide hover:border-purple-500 hover:text-purple-300 transition-colors"
          >
            {t.hero.cta_donate}
          </button>
        </motion.div>
      </motion.div>

      <motion.button
        onClick={() => scrollTo("about")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 text-white/50"
        data-testid="hero-scroll-btn"
      >
        <span className="text-[10px] uppercase tracking-[0.25em]">{t.hero.scroll}</span>
        <motion.span animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 1.6 }}>
          <ArrowDown size={18} />
        </motion.span>
      </motion.button>
    </section>
  );
};
